import { Box, Chip, Typography, Link } from "@mui/material";
import { ImageWithLoader } from "./ImageWithLoader";
export const renderInline = (inline: any, idx: number): JSX.Element | null => {
  const content = Array.isArray(inline.content)
    ? inline.content.map((inl: any, i: number) => renderInline(inl, i))
    : inline.content;

  switch (inline.type) {
    case "text":
      return <span key={idx}>{content}</span>;

    case "bold":
      return <strong key={idx}>{content}</strong>;

    case "italic":
      return <em key={idx}>{content}</em>;

    case "underline":
      return (
        <Box key={idx} component="span" sx={{ textDecoration: "underline" }}>
          {content}
        </Box>
      );

    case "bold_italic":
      return (
        <Box
          key={idx}
          component="span"
          sx={{ fontWeight: 600, fontStyle: "italic" }}
        >
          {content}
        </Box>
      );

    case "bold_underline":
      return (
        <Box
          key={idx}
          component="span"
          sx={{ fontWeight: 600, textDecoration: "underline" }}
        >
          {content}
        </Box>
      );

    case "italic_underline":
      return (
        <Box
          key={idx}
          component="span"
          sx={{ fontStyle: "italic", textDecoration: "underline" }}
        >
          {content}
        </Box>
      );

    case "bold_italic_underline":
      return (
        <Box
          key={idx}
          component="span"
          sx={{
            fontWeight: 600,
            fontStyle: "italic",
            textDecoration: "underline",
          }}
        >
          {content}
        </Box>
      );

    case "strikethrough":
      return (
        <Box key={idx} component="span" sx={{ textDecoration: "line-through" }}>
          {content}
        </Box>
      );

    case "emoji":
      return (
        <Box key={idx} component="span" sx={{ mx: 0.3, fontSize: "1.1em" }}>
          {content}
        </Box>
      );

    case "link":
      return (
        <Link
          key={idx}
          href={inline.href}
          target="_blank"
          rel="noopener noreferrer"
          title={inline.href}
          sx={{
            mx: 0.5,
            color: "#7B1FA2",
            textDecoration: "underline",
            wordBreak: "break-word",
            cursor: "pointer",
            "&:hover": { textDecoration: "none" },
          }}
        >
          {content}
        </Link>
      );

    case "mention":
      return (
        <Chip
          key={idx}
          label={`@${inline.name}`}
          size="small"
          sx={{
            mx: 0.5,
            fontSize: 12,
            backgroundColor: "#F7EEF9",
            color: "#7B1FA2",
            height: 22,
            borderRadius: "16px",
          }}
        />
      );

    case "tag":
      return (
        <Chip
          key={idx}
          label={`[${inline.content}]`}
          size="small"
          sx={{
            mx: 0.5,
            fontSize: 12,
            backgroundColor: "#F0F0F0",
            color: "#555",
            height: 22,
          }}
        />
      );

    default:
      return null;
  }
};

// Renders one block (paragraph, list item, image, code block, file, mentions etc.)
export const renderBlock = (
  block: any,
  idx: number,
  handlers: {
    handleImageClick?: (src: string) => void;
    onFileClick?: (data: { src: string; filename: string }) => void;
  } = {}
) => {
  const { handleImageClick, onFileClick } = handlers;

  switch (block.type) {
    case "h1":
      return (
        <Typography
          key={idx}
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={{ mb: 2 }}
        >
          {block.content.map((inl: any, i: number) => renderInline(inl, i))}
        </Typography>
      );

    case "h2":
      return (
        <Typography
          key={idx}
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{ mb: 2 }}
        >
          {block.content.map((inl: any, i: number) => renderInline(inl, i))}
        </Typography>
      );

    case "h3":
      return (
        <Typography
          key={idx}
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{ mb: 2 }}
        >
          {block.content.map((inl: any, i: number) => renderInline(inl, i))}
        </Typography>
      );

    case "paragraph":
      return (
        <Box key={idx} sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
          {block.content.map((inl: any, i: number) => renderInline(inl, i))}
        </Box>
      );

    case "ordered-list-item":
      return (
        <Box key={idx} sx={{ display: "flex", gap: 1, ml: 3, mb: 0.5 }}>
          <Box component="span">{block.order}.</Box>
          <Box>
            {block.content.map((inl: any, i: number) => renderInline(inl, i))}
          </Box>
        </Box>
      );

    case "unordered-list-item":
      return (
        <Box key={idx} sx={{ display: "flex", gap: 1, ml: 3, mb: 0.5 }}>
          <Box component="span">•</Box>
          <Box>
            {block.content.map((inl: any, i: number) => renderInline(inl, i))}
          </Box>
        </Box>
      );

    case "image":
      return (
        <ImageWithLoader
          key={idx}
          src={block.src}
          filename={block.filename}
          onClick={handleImageClick}
        />
      );

    case "file":
      return (
        <Box
          key={idx}
          sx={{
            my: 1,
            p: 1,
            borderRadius: 2,
            display: "inline-block",
            textAlign: "center",
            width: 200,
            height: 150,
            border: "1px solid #ccc",
            overflow: "hidden",
            cursor: "pointer",
            mr: 1,
            "&:hover": { boxShadow: 3 },
          }}
          onClick={() =>
            onFileClick?.({ src: block.src, filename: block.filename })
          }
          //onClick={() => window.open(block.src, "_blank")}
        >
          {/* PDF Placeholder Preview */}
          <Box
            sx={{
              width: "100%",
              height: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              color: "#888",
              fontSize: 12,
            }}
          >
            📄 PDF Preview
          </Box>
          {/* File Name */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: 13,
              mt: 0.5,
              px: 1,
              wordBreak: "break-all",
            }}
          >
            {block.filename}
          </Typography>
          {/* Optional Timestamp */}
          {/* {block.timestamp && (
              <Typography variant="caption" color="text.secondary">
                {block.timestamp}
              </Typography>
            )} */}
        </Box>
      );

    case "code_block":
      return (
        <Box
          key={idx}
          component="pre"
          sx={{
            backgroundColor: "#f4f4f4",
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "8px",
            fontFamily: "monospace",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            my: 1,
          }}
        >
          {block.code || "[code_block]"}
        </Box>
      );

    case "mention":
      return (
        <Box key={idx} sx={{ mb: 1 }}>
          <Chip
            label={`@${block.name}`}
            size="small"
            sx={{
              mx: 0.5,
              fontSize: 12,
              backgroundColor: "#E8F0FE",
              color: "#1A73E8",
              height: 22,
              borderRadius: "16px",
            }}
          />
        </Box>
      );

    default:
      return null;
  }
};
